/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { Box, Dialog, DialogContent, Link, ListItem, styled, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { StyledSwitch } from "../../components/total_sales_card/styles";
import { fonts } from "../../themes/dashboard";

const FORM_BLUE = "#395BBD";

export const StyledHeadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

export const StyledDiscountCardContainer = styled(Box)({
  "@media (max-width: 1000px) ": {
    height: "100vh",
  },
  height: "100%",
  display: "flex",
  flexWrap: "wrap",
  gap: "30px",
  marginTop: "30px",
  overflowY: "scroll",
  padding: "0px 30px",
});

export const StyledBreadCrumbContainer = styled("div")({
  height: "20px",
  backgroundColor: "#f5f5f8",
  padding: "10px 0px 10px 20px",
  borderColor: "#e0e0e0",
  borderWidth: "0px 0px 1px 0px",
  borderStyle: "solid",
});

export const StyledLink = styled(Link)(({ color }) => ({
  textDecoration: "none",
  color: color,
  fontSize: "13px",
  fontWeight: "400",
}));

export const StyledTitleContainer = styled("div")({
  padding: "20px 0px 10px 0px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

export const DiscountCardTopFields = styled(Box)({
  display: "grid",
  gap: "16px",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  alignItems: "start",
  "@media (max-width: 720px)": {
    gridTemplateColumns: "1fr",
  },
});

export const ModalContainer = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#fff",
  boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
  padding: "32px",
  minWidth: 400,
  borderRadius: "4px",
  width: "60%",
  height: "70%",
});

export const StyledCheckIcon = styled(CheckIcon)({
  fontSize: "1rem",
  color: "#395BBD",
});

export const StyledListItem = styled(ListItem)({
  alignItems: "flex-start",
  gap: "10px",
  paddingLeft: 0,
});

export const StyledTypography = styled(Typography)({
  fontWeight: 600,
});

export const DiscountCardNameContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: "10px",
  height: "10%",
});

export const DiscountCardContentContainer = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  height: "90%",
  gap: "10px",
  flexDirection: 'column'
});

export const DiscountCardImageContainer = styled(Box)({
  width: "50%",
  height: "90%",
  borderRadius: "10px",
});

export const EditDiscountCardDialog = styled(Dialog)({
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(15, 23, 42, 0.45)",
  },
  "& .MuiDialog-paper": {
    width: "min(960px, calc(100vw - 32px))",
    maxWidth: "960px",
    height: "calc(100vh - 48px)",
    maxHeight: "calc(100vh - 48px)",
    margin: "24px",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundImage: "none",
  },
});

export const EditDiscountCardDialogHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "16px 20px",
  borderBottom: "1px solid #e5e7eb",
});

export const EditDiscountCardDialogTitle = styled(Typography)({
  fontSize: "22px",
  fontWeight: 700,
  lineHeight: 1.2,
  color: "rgba(81, 81, 82, 1)",
});

export const EditDiscountCardDialogContent = styled(DialogContent)({
  padding: 0,
  overflowY: "auto",
  height: "100%",
});

export const LoaderContainer = styled("div")({
  height: "100%",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

export const StyledContentWrapper = styled('div')({
  border: '1px solid #e0e0e0',
})

export const ContentItem = styled('div')({
  height: '50px',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  fontWeight: '600',
  border: '1px solid #e0e0e0',
  paddingLeft: '20px'
})

export const DiscountCardNameWrapper = styled('div')({
  display: 'flex', 
  justifyContent: 'center', 
  alignItems: 'center', 
  gap: '10px',
})

export const CircularColor = styled('div')({
  height: '15px', 
  width: '15px', 
  borderRadius: '50%',
})

export const DiscountCardTitle = styled(Typography)({
  fontWeight: '600',
})

export const StyledRuleContainer = styled('div')({
  minHeight: '40px',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'stretch !important'
})

export const StyledRule = styled('div')({
  width: '33.4%',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: '#e0e0e0',
  display: 'flex',
  alignItems: 'center',
  padding: '0px 20px',
  boxSizing: 'border-box',
  opacity: '0.8'
})

export const DisclaimerContainer = styled('div')({
  marginTop: '20px'
})

// Auto-assign row, laid out like the policy draft banner and tinted blue when on.
export const AutoAssignBanner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOn",
})(({ isOn }) => ({
  alignItems: "center",
  backgroundColor: isOn ? "#eef2fb" : "#fbfbfa",
  border: `1px solid ${isOn ? FORM_BLUE : "#e4e4e7"}`,
  borderRadius: "8px",
  display: "flex",
  gap: "10px",
  justifyContent: "space-between",
  marginTop: "12px",
  padding: "10px 12px",
}));

export const AutoAssignTitle = styled(Typography)({
  color: "#18181b",
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
});

export const AutoAssignHelperText = styled(Typography)({
  color: "#52525b",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
});

// The policy switch in the form's blue instead of green.
export const AutoAssignSwitch = styled(StyledSwitch)({
  flexShrink: 0,
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: FORM_BLUE,
  },
  "& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb": {
    color: FORM_BLUE,
  },
});
